import React, { useRef, useEffect } from 'react';
import { Button } from '../../BasicComponents/Button/Button';
import { currencySymbol } from '../../../utils/currency';
import type { NumPadProps } from './types';

// ─── NumPad ───────────────────────────────────────────────────────────────────

export function NumPad({
  value,
  onChange,
  onSubmit,
  maxValue,
  allowDecimal = false,
  currency = false,
  currencyCode = 'INR',
  label,
  compact = false,
  className,
}: NumPadProps) {
  const symbol = currencySymbol(currencyCode);
  // Key box size — compact hosts trade touch target for a modal that fits.
  const keySize = compact ? 'min-h-[42px] min-w-[42px]' : 'min-h-[56px] min-w-[56px]';
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus the input when the numpad mounts
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  const numericValue = parseFloat(value) || 0;
  const isMaxReached = maxValue !== undefined && numericValue >= maxValue;
  const hasError = maxValue !== undefined && numericValue > maxValue;

  function handleKey(key: string) {
    if (isMaxReached && key !== 'backspace') return;

    if (key === 'backspace') {
      const next = value.slice(0, -1);
      onChange(next);
      return;
    }

    if (key === '.') {
      if (!allowDecimal) return;
      if (value.includes('.')) return;
      onChange(value + '.');
      return;
    }

    const next = value + key;
    const nextNumeric = parseFloat(next);
    if (maxValue !== undefined && nextNumeric > maxValue) return;
    onChange(next);
  }

  function handleSubmit() {
    onSubmit?.(value);
  }

  const topKeys = ['7', '8', '9', '4', '5', '6', '1', '2', '3'];

  return (
    <div className={`flex flex-col gap-2 w-full select-none ${className ?? ''}`}>
      {/* Label */}
      {label && (
        <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">{label}</span>
      )}

      {/* Value input — supports both keyboard typing and numpad clicks */}
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={currency ? `${symbol}${value || '0'}` : value || '0'}
        onChange={(e) => {
          let raw = e.target.value;
          if (currency && raw.startsWith(symbol)) raw = raw.slice(symbol.length);
          raw = raw.replace(/[^0-9.]/g, '');
          if (raw === '0' && value === '') return;
          if (raw.startsWith('0') && raw.length > 1 && raw[1] !== '.') raw = raw.slice(1);
          if (raw.split('.').length > 2) return;
          if (!allowDecimal && raw.includes('.')) return;
          if (maxValue !== undefined && parseFloat(raw) > maxValue) return;
          onChange(raw);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit?.(value);
        }}
        aria-live="polite"
        aria-label={label ?? 'Numeric input'}
        className={[
          'w-full rounded-md border px-4 py-3 text-right text-2xl font-bold font-mono transition-colors outline-none',
          'focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
          hasError
            ? 'border-danger-500 bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400'
            : 'border-neutral-300 bg-white text-neutral-900 dark:bg-neutral-900 dark:border-neutral-600 dark:text-white',
        ]
          .filter(Boolean)
          .join(' ')}
      />

      {/* Max reached warning */}
      {isMaxReached && !hasError && (
        <p className="text-xs text-warning-600 dark:text-warning-400">Maximum value reached</p>
      )}

      {/* Key grid: 3×3 (7-9, 4-6, 1-3) */}
      <div className="grid grid-cols-3 gap-1.5">
        {topKeys.map((k) => (
          <button
            key={k}
            type="button"
            aria-label={k}
            disabled={isMaxReached}
            onClick={() => handleKey(k)}
            className={[
              'flex items-center justify-center rounded-md text-xl font-semibold transition-colors',
              keySize,
              'bg-white border border-neutral-200 text-neutral-800',
              'hover:bg-neutral-50 active:bg-neutral-100',
              'dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-100',
              'dark:hover:bg-neutral-700 dark:active:bg-neutral-600',
              isMaxReached ? 'opacity-40 cursor-not-allowed pointer-events-none' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {k}
          </button>
        ))}
      </div>

      {/* Bottom row: decimal / 0 / backspace */}
      <div className="grid grid-cols-3 gap-1.5">
        {/* Decimal or empty */}
        {allowDecimal ? (
          <button
            type="button"
            aria-label="Decimal point"
            disabled={value.includes('.')}
            onClick={() => handleKey('.')}
            className={[
              'flex items-center justify-center rounded-md text-xl font-semibold transition-colors',
              keySize,
              'bg-white border border-neutral-200 text-neutral-800',
              'hover:bg-neutral-50 active:bg-neutral-100',
              'dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-100',
              'dark:hover:bg-neutral-700 dark:active:bg-neutral-600',
              value.includes('.') ? 'opacity-40 cursor-not-allowed pointer-events-none' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            .
          </button>
        ) : (
          <div />
        )}

        {/* 0 */}
        <button
          type="button"
          aria-label="0"
          disabled={isMaxReached}
          onClick={() => handleKey('0')}
          className={[
            'flex items-center justify-center rounded-md text-xl font-semibold transition-colors',
            keySize,
            'bg-white border border-neutral-200 text-neutral-800',
            'hover:bg-neutral-50 active:bg-neutral-100',
            'dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-100',
            'dark:hover:bg-neutral-700 dark:active:bg-neutral-600',
            isMaxReached ? 'opacity-40 cursor-not-allowed pointer-events-none' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          0
        </button>

        {/* Backspace */}
        <button
          type="button"
          aria-label="Delete"
          onClick={() => handleKey('backspace')}
          className={[
            'flex items-center justify-center rounded-md text-xl font-semibold transition-colors',
            keySize,
            'bg-neutral-100 border border-neutral-200 text-neutral-700',
            'hover:bg-neutral-200 active:bg-neutral-300',
            'dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-200',
            'dark:hover:bg-neutral-600 dark:active:bg-neutral-500',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          ⌫
        </button>
      </div>

      {/* Enter / Confirm — full width. Suppressed when no `onSubmit` is wired:
          hosts like the payment modal own the confirm action themselves, so
          this rendered a dead full-width button that both wasted vertical
          space and competed with the real Confirm button. */}
      {onSubmit && (
        <Button
          variant="primary"
          size="lg"
          fullWidth
          aria-label="Confirm"
          onClick={handleSubmit}
          disabled={!value || value === '0'}
        >
          Enter
        </Button>
      )}
    </div>
  );
}
